import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { ErrorHandlerService } from '@y42/error-handling';
import { JobStatus } from '@y42/json-schema';
import { isNotNullish, isNullish } from '@y42/util/nullish';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { DataTestableQuery } from '../../../state';
import { DataTestDialogService } from '../../data-test-dialog/data-test-dialog.service';
import { DataTestShowDialogCellRendererParam } from './data-test-show-dialog-cell-renderer.model';

enum LeafNodeDisplayMode {
  NoTests = 'no-tests',
  TestsExist = 'tests-exist',
  AddButton = 'add-button',
}

@UntilDestroy()
@Component({
  selector: 'y42-data-test-show-dialog-cell-renderer[path]',
  templateUrl: './data-test-show-dialog-cell-renderer.component.html',
  styleUrls: ['./data-test-show-dialog-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DataTestDialogService],
})
export class DataTestShowDialogCellRendererComponent implements ICellRendererAngularComp {
  constructor(
    private readonly dataTestDialogService: DataTestDialogService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly datasourceTableQuery: DataTestableQuery,
  ) {}
  private readonly params$$ = new BehaviorSubject<ICellRendererParams<
    unknown,
    DataTestShowDialogCellRendererParam
  > | null>(null);

  private readonly leafNodeDisplayMode$: Observable<LeafNodeDisplayMode> = this.params$$.pipe(
    map((param) => param?.value?.$path),
    filter((path) => isNotNullish(path) && path !== ''),
    switchMap((path) => this.datasourceTableQuery.selectUIEntity(path!)),
    map((table): LeafNodeDisplayMode => {
      if (table?.tests && table.tests.tests.length > 0) {
        return LeafNodeDisplayMode.TestsExist;
      }
      if (table?.latest_valid_job?.status === JobStatus.Ready && (!table?.tests || table.tests.tests.length === 0)) {
        return LeafNodeDisplayMode.AddButton;
      }

      return LeafNodeDisplayMode.NoTests;
    }),
    startWith(LeafNodeDisplayMode.NoTests),
  );

  readonly state = connectState(this, {
    isGroup: this.params$$.pipe(map((params) => params?.node.group === true)),
    path: this.params$$.pipe(map((params) => params?.value?.$path ?? null)),
    testCount: this.params$$.pipe(map((params) => params?.value?.dataTestCount ?? 0)),
    testCountText: this.params$$.pipe(
      map((params) => params?.value?.dataTestCount ?? 0),
      map((count) => `${count}  ${count > 1 ? 'Tests' : 'Test'}`),
    ),
    leafNodeDisplayMode: this.leafNodeDisplayMode$,
  });

  agInit(params: ICellRendererParams<unknown, DataTestShowDialogCellRendererParam>): void {
    this.updateParams(params);
  }

  refresh(params: ICellRendererParams<unknown, DataTestShowDialogCellRendererParam>): boolean {
    this.updateParams(params);
    return true;
  }

  async openDialogClick() {
    const path = this.state.path;
    if (isNullish(path)) {
      this.errorHandlerService.error('DataTestShowDialogCellRendererComponent.openDialogClick: path is null.');
      return;
    }
    await this.dataTestDialogService.open({
      path,
    });
  }

  private updateParams(params: ICellRendererParams<unknown, DataTestShowDialogCellRendererParam>): void {
    this.params$$.next(params);
  }
}
